import React from 'react'
import { graphql } from 'gatsby'
import { Box } from '@mui/material'

import { FeaturedContent, ContentProps } from '@components/FeaturedContent'
import { Hits } from '@components/CustomHitsThreeRow'
import { Layout, LayoutProps } from '@components/Layout'
import { GettingStartedBanner } from '@components/GettingStartedBanner'
import { Pagination } from '@components/Blog/Pagination'
import { webPageLdJson } from '~/utils/common'

const CASE_STUDIES_PER_PAGE = 9
const META_TITLE = 'RudderStack Customers | Rudderstack.com'

type CaseStudy = {
  title: string
  slug: { current: string }
  listing_image: { asset: { url: string } }
}

type CustomersProps = {
  data: {
    allSanityCaseStudies: {
      totalCount: number
      edges: { node: CaseStudy }[]
    }
    featuredContent: {
      edges: ContentProps[]
    }
  }
  pageContext: {
    category: string
    page: number
  }
  location: LayoutProps['location']
}

export const Head = ({
  location,
}: {
  location: CustomersProps['location']
}) => (
  <>
    <title>{META_TITLE}</title>
    <meta property='og:title' content={META_TITLE} />
    <meta property='twitter:title' content={META_TITLE} />
    <meta
      name='image'
      property='og:image'
      content='https://www.rudderstack.com/images/rudderstack_thumbnail.png'
    />

    <script type='application/ld+json'>
      {JSON.stringify(webPageLdJson(META_TITLE, null, location.href))}
    </script>
  </>
)

const Customers = ({ data, pageContext, location }: CustomersProps) => {
  const { page } = pageContext

  /* Mapping sanity blogs to Algolia hit structure */
  const hits = data.allSanityCaseStudies.edges
    .map(x => x.node)
    .map(x => ({
      ...x,
      slug: x.slug.current,
      logoimage: x.listing_image.asset?.url,
    }))

  return (
    <Layout location={location}>
      <Box sx={{ bgcolor: 'background.paper', pt: { xs: 8.5, laptop: 13 } }}>
        <Box
          sx={{
            bgcolor: 'background.default',
            display: { xs: 'none', mobile: 'block' },
            pb: { xs: 3.5, laptop: 12 },
            pt: { xs: 7, laptop: 13 },
          }}>
          <FeaturedContent
            data={data.featuredContent.edges[0]}
            type='customers'
            sx={{ px: { xs: 4, mobile: 5, desktop: 0 } }}
          />
        </Box>

        <Box
          sx={{
            maxWidth: 1152,
            mx: 'auto',
            px: { xs: 3, mobile: 5, desktop: 0 },
            py: { xs: 3.5, mobile: 2.5, laptop: 6 },
          }}>
          <Hits
            hits={hits}
            type='customers'
            sx={{
              display: 'grid',
              mt: { xs: 5.5, mobile: 3.5, laptop: 6 },
            }}
          />

          <Pagination
            pageType='customers'
            activePage={page}
            totalPages={Math.ceil(
              data.allSanityCaseStudies.totalCount / CASE_STUDIES_PER_PAGE
            )}
          />
        </Box>

        <GettingStartedBanner />
      </Box>
    </Layout>
  )
}

export default Customers

export const query = graphql`
  query Customers($skip: Int) {
    allSanityCaseStudies(
      sort: { fields: weight, order: DESC }
      limit: 9
      skip: $skip
    ) {
      totalCount
      edges {
        node {
          title
          slug {
            current
          }
          listing_image {
            asset {
              url
            }
          }
        }
      }
    }
    featuredContent: allSanityCaseStudies(
      limit: 1
      sort: { fields: article_dttm, order: DESC }
      filter: { weight: { gt: 0 } }
    ) {
      edges {
        node {
          title
          slug {
            current
          }
          listing_image {
            asset {
              url
            }
          }
        }
      }
    }
  }
`
